export interface CertificateEmailDraft {
  subject: string;
  body: string;
  attachmentFilename: string;
  certificateKind: string;
}

export interface CertificateEmailDrafts {
  mentor: CertificateEmailDraft;
  completion: CertificateEmailDraft;
}

interface NotificationTemplate {
  subject: string;
  bodyText: string;
  attachmentFilename: string;
  certificateKind?: string;
}

interface NotificationManifest {
  templates: {
    student_completion_competent: NotificationTemplate;
    student_completion_course_only: NotificationTemplate;
  };
}

function applyPlaceholders(content: string, vars: Record<string, string>): string {
  let output = content;
  for (const [placeholder, value] of Object.entries(vars)) {
    output = output.replaceAll(placeholder, value);
  }
  return output;
}

function buildDraftFromTemplate(
  template: NotificationTemplate,
  vars: Record<string, string>,
): CertificateEmailDraft {
  return {
    subject: applyPlaceholders(template.subject, vars),
    body: applyPlaceholders(template.bodyText, vars),
    attachmentFilename: template.attachmentFilename,
    certificateKind: template.certificateKind ?? 'completion',
  };
}

function formatIssueDateLabel(): string {
  return new Date().toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function buildCertificateEmailDraftVars(input: {
  studentName: string;
  studentKey: string;
  issueDate?: string;
}): Record<string, string> {
  return {
    '[Student Name]': input.studentName.trim() || 'Mentology Graduate',
    '[Unique ID]': input.studentKey.trim().toUpperCase(),
    '[Issue Date]': input.issueDate || formatIssueDateLabel(),
  };
}

export async function loadCertificateEmailDrafts(input: {
  studentName: string;
  studentKey: string;
  issueDate?: string;
}): Promise<CertificateEmailDrafts> {
  const vars = buildCertificateEmailDraftVars(input);
  const response = await fetch('/Assets/Notifications/notification-templates.json');
  if (!response.ok) {
    throw new Error('notification_templates_missing');
  }

  const manifest = await response.json() as NotificationManifest;
  return {
    mentor: buildDraftFromTemplate(manifest.templates.student_completion_competent, vars),
    completion: buildDraftFromTemplate(manifest.templates.student_completion_course_only, vars),
  };
}
